import {
  collection,
  doc,
  getDoc,
  setDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

export const NIGERIAN_STATES = [
  "Abia",
  "Adamawa",
  "Akwa Ibom",
  "Anambra",
  "Bauchi",
  "Bayelsa",
  "Benue",
  "Borno",
  "Cross River",
  "Delta",
  "Ebonyi",
  "Edo",
  "Ekiti",
  "Enugu",
  "FCT",
  "Gombe",
  "Imo",
  "Jigawa",
  "Kaduna",
  "Kano",
  "Katsina",
  "Kebbi",
  "Kogi",
  "Kwara",
  "Lagos",
  "Nasarawa",
  "Niger",
  "Ogun",
  "Ondo",
  "Osun",
  "Oyo",
  "Plateau",
  "Rivers",
  "Sokoto",
  "Taraba",
  "Yobe",
  "Zamfara",
];

export const DEFAULT_SHIPPING_SETTINGS = {
  defaultDeliveryFee: 3000,
  freeShippingThreshold: 50000,
  enableFreeShipping: true,
  pickupFee: 0, // Free pickup
  deliveryDays: 7, // Delivered within 7 days
  pickupDays: 5,
  stateRates: {
    "Lagos": 2000,
    "Ogun": 2500,
    "Oyo": 2500,
    "Osun": 2800,
    "Ondo": 3000,
    "Ekiti": 3000,
    "FCT": 3500,
    "Rivers": 3800,
    "Delta": 3500,
    "Edo": 3200,
    "Anambra": 3500,
    "Enugu": 3500,
    "Imo": 3500,
    "Abia": 3500,
    "Akwa Ibom": 3800,
    "Cross River": 4000,
    "Kano": 4000,
    "Kaduna": 3800,
    "Kwara": 3000,
    "Plateau": 4000,
  },
};

/**
 * Fetch global shipping settings from Firestore (or fallback to defaults)
 */
export async function getShippingSettings() {
  try {
    const snap = await getDoc(doc(db, "shipping_settings", "general"));
    if (snap.exists()) {
      const data = snap.data();
      return {
        ...DEFAULT_SHIPPING_SETTINGS,
        ...data,
        stateRates: {
          ...DEFAULT_SHIPPING_SETTINGS.stateRates,
          ...(data.stateRates || {}),
        },
      };
    }
  } catch (err) {
    console.warn("Could not load remote shipping settings, using defaults:", err);
  }
  return DEFAULT_SHIPPING_SETTINGS;
}

/**
 * Save global shipping settings to Firestore (Admin only)
 */
export async function saveShippingSettings(settings) {
  await setDoc(
    doc(db, "shipping_settings", "general"),
    {
      ...settings,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/**
 * Get pickup locations (optionally filtered by state and active status)
 */
export async function getPickupLocations(stateFilter = null, onlyActive = true) {
  try {
    const locRef = collection(db, "pickup_locations");
    let q = locRef;

    if (stateFilter && onlyActive) {
      q = query(locRef, where("state", "==", stateFilter), where("active", "==", true));
    } else if (stateFilter) {
      q = query(locRef, where("state", "==", stateFilter));
    } else if (onlyActive) {
      q = query(locRef, where("active", "==", true));
    }

    const snap = await getDocs(q);
    const locations = snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    // Sort alphabetically by name
    return locations.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  } catch (err) {
    console.warn("Error fetching pickup locations:", err);
    return [];
  }
}

/**
 * Add a new pickup location (Admin)
 */
export async function addPickupLocation(data) {
  return await addDoc(collection(db, "pickup_locations"), {
    name: data.name.trim(),
    state: data.state.trim(),
    city: data.city.trim(),
    address: data.address.trim(),
    phone: data.phone?.trim() || "",
    operatingHours: data.operatingHours?.trim() || "Mon - Sat: 9:00 AM - 6:00 PM",
    notes: data.notes?.trim() || "",
    active: data.active !== false,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * Update an existing pickup location (Admin)
 */
export async function updatePickupLocation(id, data) {
  await updateDoc(doc(db, "pickup_locations", id), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Delete a pickup location (Admin)
 */
export async function deletePickupLocation(id) {
  await deleteDoc(doc(db, "pickup_locations", id));
}

/**
 * Calculate final shipping fee based on fulfillment type, state, cart items, and settings.
 *
 * Rules:
 * 1. Fulfillment Type "pickup":
 *    - Base fee is settings.pickupFee (default 0).
 * 2. Fulfillment Type "delivery":
 *    - Check for product specific shipping:
 *      - If item.shippingType === "free": item ships free.
 *      - If all items in cart have shippingType === "free", overall delivery fee is 0.
 *      - If item.shippingType === "fixed" (e.g. custom product rate): adds product fixed shipping.
 *      - If item.shippingType === "surcharge" (e.g. heavy item surcharge like battery/inverter): adds surcharge.
 *    - If subtotal >= settings.freeShippingThreshold and settings.enableFreeShipping is on:
 *      - Base location fee is waived (₦0). Any heavy product surcharges still apply.
 *    - Otherwise:
 *      - Base location fee is determined by state (stateRates[state] || defaultDeliveryFee).
 *      - Plus any product-specific surcharges.
 */
export function calculateShippingFee({
  fulfillmentType = "delivery",
  state = "",
  cart = [],
  subtotal = 0,
  settings = DEFAULT_SHIPPING_SETTINGS,
}) {
  if (fulfillmentType === "pickup") {
    const fee = Number(settings?.pickupFee ?? 0);
    return {
      fee,
      isFree: fee === 0,
      breakdown: fee === 0 ? "Free Station Pickup" : `Station Pickup Fee`,
      estimatedDays: settings?.pickupDays || 5,
    };
  }

  // Delivery calculation
  if (!state) {
    // If state is not selected yet, return fallback base delivery fee or 0 if qualifying for free shipping
    const fee =
      settings?.enableFreeShipping && subtotal >= (settings?.freeShippingThreshold || 50000)
        ? 0
        : settings?.defaultDeliveryFee || 3000;
    return {
      fee,
      isFree: fee === 0,
      breakdown: fee === 0 ? "Free Delivery" : "Estimated Standard Delivery",
      estimatedDays: settings?.deliveryDays || 7,
    };
  }

  // Check product-level rules
  let allItemsFree = cart.length > 0;
  let totalProductSurcharges = 0;
  let hasFixedItemShipping = false;
  let fixedItemShippingTotal = 0;

  for (const item of cart) {
    const qty = Number(item.qty || 1);
    if (item.shippingType !== "free") {
      allItemsFree = false;
    }

    if (item.shippingType === "fixed" && item.shippingFee) {
      hasFixedItemShipping = true;
      fixedItemShippingTotal += Number(item.shippingFee) * qty;
    } else if (item.shippingType === "surcharge" && item.shippingSurcharge) {
      totalProductSurcharges += Number(item.shippingSurcharge) * qty;
    }
  }

  if (cart.length > 0 && allItemsFree) {
    return {
      fee: 0,
      isFree: true,
      breakdown: "Free Shipping (Item special)",
      estimatedDays: settings?.deliveryDays || 7,
    };
  }

  // Base state rate
  const stateRate = settings?.stateRates?.[state] ?? settings?.defaultDeliveryFee ?? 3000;

  // Free shipping check for order total
  const isFreeDeliveryQualified =
    settings?.enableFreeShipping && subtotal >= (settings?.freeShippingThreshold || 50000);

  let finalFee = 0;
  let breakdown = "";

  if (hasFixedItemShipping) {
    finalFee = fixedItemShippingTotal + totalProductSurcharges;
    breakdown = "Product-specific Delivery Rate";
  } else if (isFreeDeliveryQualified) {
    finalFee = totalProductSurcharges; // base fee is 0, only surcharges apply if any
    breakdown =
      totalProductSurcharges > 0
        ? "Free Standard Delivery + Heavy Item Surcharge"
        : "Free Delivery 🎉";
  } else {
    finalFee = stateRate + totalProductSurcharges;
    breakdown =
      totalProductSurcharges > 0
        ? `Delivery to ${state} + Heavy Item Surcharge`
        : `Standard Delivery to ${state}`;
  }

  return {
    fee: finalFee,
    isFree: finalFee === 0,
    breakdown,
    baseRate: stateRate,
    surcharges: totalProductSurcharges,
    estimatedDays: settings?.deliveryDays || 7,
  };
}

/**
 * Format the 7-day delivery deadline from an order date
 */
export function getEstimatedDeliveryWindow(fromDate = new Date(), days = 7) {
  const start = fromDate?.toDate ? fromDate.toDate() : new Date(fromDate || Date.now());
  const deadline = new Date(start.getTime() + days * 24 * 60 * 60 * 1000);

  const formattedDeadline = deadline.toLocaleDateString("en-NG", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  return {
    deadline,
    days,
    text: `Within ${days} days (by ${formattedDeadline})`,
    dateText: formattedDeadline,
  };
}
